import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, KeyRound, Loader2, LogIn } from 'lucide-react';
import { api, ApiError } from '../api/client';
import { takeSignedOutMessage } from '../api/session-end';
import { CodeInput } from '../components/Mfa';
import { queryKeys } from '../api/queryKeys';

/** A build can still switch the hint off outright; otherwise it follows whether the demo accounts are seeded. */
const DEMO_ALLOWED = import.meta.env.VITE_PRISM_DEMO_HINTS !== 'false';

export interface AuthHints {
  demoAccounts: boolean;
  /** Single sign-on buttons (1.0.0-rc.4): none unless an administrator set SSO up. */
  sso?: Array<{ id: string; name: string }>;
  /** "Forgot password" works: email is set up. */
  passwordReset?: boolean;
  /** First-run setup is waiting. */
  setup?: { reason: 'no_admin' | 'example_password' } | null;
  /** "Don't ask for a code on this device for N days" at the code step (1.0.0-rc.5); 0: not offered. */
  mfaRememberDays?: number;
}

export const useAuthHints = () => useQuery({ queryKey: ['auth-hints'], queryFn: () => api<AuthHints>('/api/v1/auth/hints'), retry: false });

/** Why a single sign-on did not work, from the #sso-error=… the API sends the browser back with. */
const SSO_ERRORS: Record<string, string> = {
  unknown_provider: 'That sign-in option is not available.',
  provider_unreachable: 'Your organisation’s sign-in service could not be reached. Try again in a moment.',
  state_expired: 'The sign-in took too long or was already used. Start again.',
  wrong_browser: 'The sign-in was started in another browser or window. Start again here.',
  provider_error: 'Your organisation’s sign-in service refused the sign-in.',
  token_invalid: 'The answer from your organisation’s sign-in service could not be verified, so it was refused.',
  no_email: 'Your organisation’s sign-in service did not tell PRISM your email address.',
  email_unverified: 'Your organisation’s sign-in service has not verified your email address.',
  account_disabled: 'Your PRISM account is disabled. Ask an administrator.',
  account_closed: 'Your PRISM account was deleted.',
  no_account: 'There is no PRISM account for you yet. Ask an administrator to add you.',
  // 1.8.1: single sign-on does not add back a person an administrator removed from the workspace.
  member_removed: 'An administrator removed you from this workspace. Ask them to add you again.',
  // 1.0.0-rc.14: an account with a password or administrator rights is never linked by email alone.
  link_required: 'This account must be linked from your profile while signed in: sign in with your PRISM password, then choose My account → Link single sign-on.',
  test_needs_admin: 'Only a signed-in administrator can test single sign-on.',
};

/** The sign-in page's frame: the picture on the left, the form on the right. */
export function LoginFrame({ children, wide = false }: { children: ReactNode; wide?: boolean }) {
  return (
    <div className="login">
      <div className="login-art" aria-hidden>
        <div className="brand"><img src="/prism.svg" width={30} height={30} alt="" /> PRISM</div>
        <div className="promise">Find any moment. <span>Make it a story.</span> Deliver it anywhere.</div>
        <div className="strip">{Array.from({ length: 5 }, (_, i) => <div key={i} />)}</div>
      </div>
      <div className="login-form-wrap">
        <div className="login-form" style={wide ? { width: 'min(440px, 100%)' } : undefined}>{children}</div>
      </div>
    </div>
  );
}

function hashParam(name: string): string | null {
  const m = new RegExp(`(?:^#|&)${name}=([^&]*)`).exec(window.location.hash);
  return m ? decodeURIComponent(m[1]!) : null;
}

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // The second step (1.0.0-rc.4): after the password, or after single sign-on (#mfa=… in the address).
  const [challenge, setChallenge] = useState<string | null>(() => hashParam('mfa'));
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  // "Don't ask for a code on this device for N days" (1.0.0-rc.5): the days from the sign-in answer, or the hints.
  const [rememberDevice, setRememberDevice] = useState(false);
  const [rememberDays, setRememberDays] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(() => {
    const sso = hashParam('sso-error');
    return sso ? SSO_ERRORS[sso] ?? 'Single sign-on did not work.' : null;
  });
  // Shown once after an automatic sign-out (the idle timeout).
  const [notice] = useState(takeSignedOutMessage);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const from = (useLocation().state as { from?: string } | null)?.from;
  const hints = useAuthHints();
  const DEMO = DEMO_ALLOWED && hints.data?.demoAccounts === true;
  const sso = hints.data?.sso ?? [];
  const returnTo = from && from !== '/login' ? from : '/';
  // The challenge or error came in the address's fragment: read once, then out of the address bar and history.
  useEffect(() => {
    if (hashParam('mfa') || hashParam('sso-error')) window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }, []);

  const signedIn = async (to?: string | null) => {
    await qc.invalidateQueries({ queryKey: queryKeys.me() });
    // Back to the page that was asked for, else the landing page (phones are sent on to /m from there).
    navigate(to || returnTo, { replace: true });
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ ok: boolean; mfaRequired?: boolean; challenge?: string; rememberDays?: number }>('/api/v1/auth/login', { method: 'POST', json: { email, password, returnTo } });
      if (r.mfaRequired && r.challenge) {
        setChallenge(r.challenge);
        if (typeof r.rememberDays === 'number') setRememberDays(r.rememberDays);
        setPassword('');
        return;
      }
      await signedIn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const remember = days > 0 && rememberDevice ? { rememberDevice: true } : {};
      const body = useRecovery ? { challenge, recoveryCode: code, ...remember } : { challenge, code: code.replace(/\s/g, ''), ...remember };
      const r = await api<{ ok: boolean; returnTo?: string | null; recoveryCodesLeft?: number }>('/api/v1/auth/mfa', { method: 'POST', json: body });
      await signedIn(r.returnTo);
    } catch (err) {
      const e2 = err as ApiError;
      if (e2.code === 'MFA_CHALLENGE_EXPIRED' || /Sign in again/.test(e2.message)) setChallenge(null);
      setError(e2.message ?? 'That code did not work');
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  if (hints.data?.setup?.reason === 'no_admin') return <FirstAdmin />;
  const days = rememberDays ?? hints.data?.mfaRememberDays ?? 0;

  if (challenge) {
    return (
      <LoginFrame>
        <form className="login-form" style={{ width: '100%' }} onSubmit={submitCode} data-testid="mfa-challenge">
          <h1>Two-step sign-in</h1>
          <p className="muted" style={{ margin: 0 }}>{useRecovery ? 'Enter one of your recovery codes. Each works once.' : 'Enter the 6-digit code from your authenticator app.'}</p>
          {error && <div className="banner error" role="alert">{error}</div>}
          <div className="field">
            <label htmlFor="mfa-login-code">{useRecovery ? 'Recovery code' : 'Code'}</label>
            {useRecovery
              ? <input id="mfa-login-code" className="input mono" autoComplete="off" value={code} onChange={(e) => setCode(e.target.value)} placeholder="abcd-efgh-ijkl-mnop" required autoFocus />
              : <CodeInput id="mfa-login-code" value={code} onChange={setCode} />}
          </div>
          {days > 0 && (
            <label className="toggle-row" style={{ margin: 0 }}>
              <input type="checkbox" checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} data-testid="mfa-remember-device" />
              <span>Don’t ask for a code on this device for {days} day{days === 1 ? '' : 's'}<br />
                <span className="faint" style={{ fontSize: 12 }}>Only from this network. Your password is still asked for. Not on a shared computer.</span></span>
            </label>
          )}
          <button className="btn primary" type="submit" disabled={busy || !code.trim()} style={{ height: 38 }} data-testid="mfa-submit">
            {busy ? <Loader2 size={16} className="spin" /> : <>Continue <ArrowRight size={16} /></>}
          </button>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button type="button" className="link-button" onClick={() => { setUseRecovery(!useRecovery); setCode(''); setError(null); }} data-testid="mfa-use-recovery">
              {useRecovery ? 'Use a code from the app' : 'Use a recovery code'}</button>
            <button type="button" className="link-button" onClick={() => { setChallenge(null); setCode(''); setError(null); }}>Start again</button>
          </div>
        </form>
      </LoginFrame>
    );
  }

  return (
    <LoginFrame>
      <form className="login-form" style={{ width: '100%' }} onSubmit={submit}>
        <h1>Sign in to PRISM</h1>
        <p className="muted" style={{ margin: 0 }}>Your media, proxies and metadata, wherever the originals live.</p>
        {hints.data?.setup?.reason === 'example_password' && (
          <div className="banner info" data-testid="setup-banner"><div className="banner-body"><strong>First-run setup</strong>
            <span>Sign in with the administrator account from your <code>.env</code> (PRISM_BOOTSTRAP_ADMIN_EMAIL and its password). PRISM then walks you through the rest.</span></div></div>
        )}
        {notice && !error && <div className="banner info" role="status" data-testid="signed-out-notice">{notice}</div>}
        {error && <div className="banner error" role="alert">{error}</div>}
        {sso.length > 0 && (
          <div className="sso-buttons" data-testid="sso-buttons">
            {sso.map((p) => (
              <a key={p.id} className="btn sso-button" href={`/api/v1/auth/sso/${p.id}/start?returnTo=${encodeURIComponent(returnTo)}`} data-testid="sso-button">
                <LogIn size={15} /> Sign in with {p.name}</a>
            ))}
            <div className="or-line"><span>or with your PRISM password</span></div>
          </div>
        )}
        <div className="field">
          <label htmlFor="email">Email</label>
          <input id="email" className="input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus={!sso.length} />
        </div>
        <div className="field">
          <label htmlFor="password">Password</label>
          <input id="password" className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </div>
        <button className="btn primary" type="submit" disabled={busy} style={{ height: 38 }}>
          {busy ? <Loader2 size={16} className="spin" /> : <>Sign in <ArrowRight size={16} /></>}
        </button>
        {hints.data?.passwordReset && <Link to="/forgot-password" className="faint" style={{ fontSize: 12.5, alignSelf: 'flex-start' }} data-testid="forgot-password">Forgot your password?</Link>}
        {DEMO && (
          <div className="demo-accounts">
            <span>Local evaluation accounts use the passwords from your <code>.env</code> file:</span>
            {['admin@prism.local', 'editor@prism.local', 'viewer@prism.local'].map((a) => (
              <button type="button" key={a} onClick={() => setEmail(a)}>{a}</button>
            ))}
          </div>
        )}
      </form>
    </LoginFrame>
  );
}

/** First-run setup with no administrator at all: the setup code from the API's log, then the first administrator. */
function FirstAdmin() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [f, setF] = useState({ setupCode: '', email: '', displayName: '', password: '', again: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (f.password !== f.again) return setError('The passwords are not the same');
    setBusy(true);
    setError(null);
    try {
      await api('/api/v1/setup/admin', { method: 'POST', json: { setupCode: f.setupCode, email: f.email, displayName: f.displayName, password: f.password } });
      await qc.invalidateQueries({ queryKey: ['auth-hints'] });
      await qc.invalidateQueries({ queryKey: queryKeys.me() });
      // On through the rest of setup: public URL, storage, email and single sign-on.
      navigate('/setup', { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <LoginFrame wide>
      <form className="login-form" style={{ width: '100%' }} onSubmit={submit} data-testid="setup-first-admin">
        <h1>Welcome to PRISM</h1>
        <p className="muted" style={{ margin: 0 }}>This installation has no administrator yet. Create one to start setting PRISM up.</p>
        {error && <div className="banner error" role="alert">{error}</div>}
        <div className="field"><label htmlFor="setup-code">Setup code</label>
          <input id="setup-code" className="input mono" value={f.setupCode} onChange={(e) => setF({ ...f, setupCode: e.target.value })} required autoFocus />
          <span className="help">It is in the API’s log: <code>docker compose logs api | grep "setup code"</code>, or run <code>docker compose exec api node src/setup-code.ts</code>.</span></div>
        <div className="field"><label htmlFor="setup-name">Your name</label><input id="setup-name" className="input" value={f.displayName} onChange={(e) => setF({ ...f, displayName: e.target.value })} required /></div>
        <div className="field"><label htmlFor="setup-email">Email</label><input id="setup-email" className="input" type="email" autoComplete="username" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required /></div>
        <div className="field"><label htmlFor="setup-pw">Password</label><input id="setup-pw" className="input" type="password" autoComplete="new-password" minLength={10} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required />
          <span className="help">At least 10 characters.</span></div>
        <div className="field"><label htmlFor="setup-pw2">Password again</label><input id="setup-pw2" className="input" type="password" autoComplete="new-password" value={f.again} onChange={(e) => setF({ ...f, again: e.target.value })} required /></div>
        <button className="btn primary" type="submit" disabled={busy} style={{ height: 38 }}>{busy ? <Loader2 size={16} className="spin" /> : <><KeyRound size={15} /> Create the administrator</>}</button>
      </form>
    </LoginFrame>
  );
}
